import React, { useEffect, useState, useRef } from 'react';
import { RefreshCw, Camera, Loader, MonitorX, Video, MousePointer2, Zap, Download, TriangleAlert } from 'lucide-react';
import useStore from '../store';
import ChromeFrame from './ChromeFrame';

const MirrorView = () => {
    const {
        currentScreenshot,
        currentMjpeg,
        isMirroring,
        captureScreenshot,
        targetId,
        isWebRtcActive,
        requestWebRtc,
        closeWebRtc,
        sendInputEvent,
        startMjpeg,
        stopMjpeg,
        exportData,
        probeTabs,
        sendCommand,
        screenshotError,
        ws
    } = useStore();

    const [streamMode, setStreamMode] = useState('STATIC'); // STATIC, MJPEG, WEBRTC
    const [inputEnabled, setInputEnabled] = useState(false);
    const [refreshInterval, setRefreshInterval] = useState(2000);

    // Scroll Sync State
    const [scrollState, setScrollState] = useState({
        scrollY: 0,
        scrollHeight: 1000,
        viewportHeight: 800
    });
    const [isDraggingScroll, setIsDraggingScroll] = useState(false);

    const videoRef = useRef(null);
    const containerRef = useRef(null);

    // Handle Stream Mode Changes
    useEffect(() => {
        if (streamMode === 'WEBRTC' && targetId) {
            requestWebRtc(targetId);
            stopMjpeg(targetId);
        } else if (streamMode === 'MJPEG' && targetId) {
            startMjpeg(targetId);
            closeWebRtc();
        } else {
            closeWebRtc();
            if (targetId) stopMjpeg(targetId);
        }
    }, [streamMode, targetId]);

    // Adaptive FPS for Static Mode
    useEffect(() => {
        let interval;
        if (streamMode === 'STATIC' && targetId) {
            const captureWithTiming = async () => {
                const start = Date.now();
                await captureScreenshot();
                const loadTime = Date.now() - start;
                setRefreshInterval(loadTime < 500 ? 1000 : loadTime < 1500 ? 2000 : 5000);
            };
            interval = setInterval(captureWithTiming, refreshInterval);
        }
        return () => clearInterval(interval);
    }, [streamMode, targetId, captureScreenshot, refreshInterval]);

    // WebSocket Message Handler for Scroll Updates
    useEffect(() => {
        if (!ws) return;

        const handleMessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === 'SCROLL_UPDATE') {
                    setScrollState({
                        scrollY: msg.scrollY,
                        scrollHeight: msg.scrollHeight,
                        viewportHeight: msg.viewportHeight
                    });
                }
            } catch (e) { }
        };

        ws.addEventListener('message', handleMessage);
        return () => ws.removeEventListener('message', handleMessage);
    }, [ws]);

    // Input Synchronization
    const handleInput = (e) => {
        if (!inputEnabled || !targetId || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const eventData = {
            type: e.type,
            x,
            y,
            button: e.button,
            key: e.key,
            keyCode: e.keyCode
        };

        sendInputEvent(eventData);
    };

    // Wheel Event for Scroll Control - Manually attached for non-passive support
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onWheel = (e) => {
            // Only handle wheel events when input control is enabled
            if (!inputEnabled || !targetId) return;

            // Prevent default browser scrolling
            e.preventDefault();

            const eventData = {
                type: 'wheel',
                deltaX: e.deltaX,
                deltaY: e.deltaY,
                deltaZ: e.deltaZ,
                deltaMode: e.deltaMode
            };

            sendInputEvent(eventData);
        };

        // Attach with passive: false to allow preventDefault()
        container.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', onWheel);
        };
    }, [inputEnabled, targetId, sendInputEvent]);

    // Scrollbar Logic
    const handleScrollDrag = (e) => {
        if (!containerRef.current || !targetId) return;

        const containerHeight = containerRef.current.clientHeight;
        const trackTop = containerRef.current.getBoundingClientRect().top;
        const clientY = e.clientY - trackTop;

        // Calculate percentage
        const percentage = Math.max(0, Math.min(1, clientY / containerHeight));
        const targetScrollY = percentage * scrollState.scrollHeight;

        sendCommand('SCROLL_TO', {
            targetId,
            x: 0,
            y: targetScrollY
        });

        // Optimistic update
        setScrollState(prev => ({ ...prev, scrollY: targetScrollY }));
    };

    const handleScrollMouseDown = (e) => {
        e.stopPropagation();
        setIsDraggingScroll(true);
        handleScrollDrag(e);
    };

    const handleScrollMouseMove = (e) => {
        if (isDraggingScroll) {
            e.preventDefault();
            handleScrollDrag(e);
        }
    };

    const handleScrollMouseUp = () => {
        setIsDraggingScroll(false);
    };

    useEffect(() => {
        if (isDraggingScroll) {
            window.addEventListener('mousemove', handleScrollMouseMove);
            window.addEventListener('mouseup', handleScrollMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleScrollMouseMove);
                window.removeEventListener('mouseup', handleScrollMouseUp);
            };
        }
    }, [isDraggingScroll]);

    // Chrome UI Navigation Handlers
    const handleNavigate = (action) => {
        if (!targetId) return;

        const actionMap = {
            'back': 'back',
            'forward': 'forward',
            'reload': 'reload',
            'home': 'url' // Special case for home
        };

        if (actionMap[action]) {
            sendCommand('NAVIGATE', {
                targetId,
                action: actionMap[action],
                url: action === 'home' ? 'chrome://newtab' : undefined
            });
        }
    };

    const handleTabSwitch = (tabId) => {
        sendCommand('SWITCH_TAB', { targetId, tabId });
        // ✅ Refresh screenshot after tab switch (with delay for tab to activate)
        setTimeout(() => captureScreenshot(), 500);
    };

    const handleTabClose = (tabId) => {
        sendCommand('CLOSE_TAB', { targetId, tabId });
    };

    const handleNewTab = () => {
        sendCommand('NEW_TAB', { targetId });
    };

    const handleAddressSubmit = (url) => {
        if (!targetId || !url) return;

        sendCommand('NAVIGATE', {
            targetId,
            action: 'url',
            url: url
        });
    };

    // Get tabs for current target
    const safeProbeTabs = probeTabs || {};
    const currentTabs = (targetId && Array.isArray(safeProbeTabs[targetId])) ? safeProbeTabs[targetId] : [];
    const currentTab = currentTabs.find(t => t && t.active) || currentTabs[0];
    const activeTargetId = targetId;

    // Calculate Scrollbar Style
    const scrollRatio = scrollState.viewportHeight / scrollState.scrollHeight;
    const thumbHeight = Math.max(20, scrollRatio * 100); // Min 20px, max 100% (using percentage logic below)
    const thumbTop = (scrollState.scrollY / scrollState.scrollHeight) * 100;
    const showScrollbar = scrollState.scrollHeight > scrollState.viewportHeight;

    if (!targetId) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 text-gray-500">
                <MonitorX size={64} className="mb-4 opacity-20" />
                <div className="text-lg font-medium">No Target Connected</div>
                <div className="text-sm">Select a target from the sidebar to start mirroring</div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-gray-950 relative overflow-hidden">
            {/* Toolbar Overlay */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-gray-800/80 backdrop-blur-sm p-1.5 rounded-full border border-gray-700 shadow-xl transition-opacity hover:opacity-100 opacity-0 group-hover:opacity-100">
                {/* Refresh Button (Static only) */}
                <button
                    onClick={() => captureScreenshot()}
                    disabled={streamMode !== 'STATIC' || isMirroring}
                    className="p-2 hover:bg-gray-700 rounded-full text-gray-300 hover:text-white transition-colors disabled:opacity-20"
                    title="Refresh Screenshot"
                >
                    <RefreshCw size={16} className={isMirroring ? 'animate-spin' : ''} />
                </button>

                <div className="w-px h-4 bg-gray-600 mx-1" />

                {/* Stream Mode Toggles */}
                <div className="flex bg-gray-900/50 rounded-full p-0.5">
                    <button
                        onClick={() => setStreamMode('STATIC')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${streamMode === 'STATIC' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        <Camera size={14} /> Static
                    </button>
                    <button
                        onClick={() => setStreamMode('MJPEG')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${streamMode === 'MJPEG' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        <Video size={14} /> MJPEG
                    </button>
                    <button
                        onClick={() => setStreamMode('WEBRTC')}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${streamMode === 'WEBRTC' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        <Zap size={14} /> WebRTC
                    </button>
                </div>

                <div className="w-px h-4 bg-gray-600 mx-1" />

                {/* Input Sync Toggle */}
                <button
                    onClick={() => setInputEnabled(!inputEnabled)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${inputEnabled
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'hover:bg-gray-700 text-gray-300'
                        }`}
                    title="Sync Mouse/Keyboard Input"
                >
                    <MousePointer2 size={14} />
                    {inputEnabled ? 'Control ON' : 'Control OFF'}
                </button>

                <div className="w-px h-4 bg-gray-600 mx-1" />

                {/* Export Button */}
                <button
                    onClick={() => exportData('network', 'json')}
                    className="p-2 hover:bg-gray-700 rounded-full text-gray-300 hover:text-white transition-colors"
                    title="Export Network Logs"
                >
                    <Download size={16} />
                </button>
            </div>

            {/* Main Content Area with Chrome UI Frame */}
            <div className="flex-1 flex items-center justify-center p-8 group relative outline-none">
                <ChromeFrame
                    currentTab={currentTab}
                    allTabs={currentTabs}
                    onNavigate={handleNavigate}
                    onTabSwitch={handleTabSwitch}
                    onTabClose={handleTabClose}
                    onNewTab={handleNewTab}
                    onAddressSubmit={handleAddressSubmit}
                >
                    <div
                        ref={containerRef}
                        className="relative w-full h-full flex items-center justify-center bg-gray-950"
                        onMouseMove={handleInput}
                        onMouseDown={handleInput}
                        onMouseUp={handleInput}
                        onClick={handleInput}
                        // onWheel handled via useEffect for passive: false support
                        onDoubleClick={handleInput}
                        tabIndex={0}
                        onKeyDown={handleInput}
                        onKeyUp={handleInput}
                        style={{ cursor: inputEnabled ? 'crosshair' : 'default', outline: 'none' }}
                    >
                        {/* Screenshot Image */}
                        {currentScreenshot ? (
                            <img
                                src={currentScreenshot}
                                alt="Remote View"
                                className="max-w-full max-h-full object-contain select-none shadow-2xl"
                                draggable={false}
                            />
                        ) : (
                            <div className="text-center p-8">
                                <div className="animate-spin mb-4 mx-auto w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full opacity-50"></div>
                                <p className="text-gray-500 text-sm">Waiting for video stream...</p>
                            </div>
                        )}

                        {/* Sync Scrollbar Overlay */}
                        {showScrollbar && (
                            <div
                                className="absolute right-1 top-0 bottom-0 w-3 z-50 group hover:bg-white/5 transition-colors rounded-full my-1"
                                onMouseDown={handleScrollMouseDown}
                            >
                                <div
                                    className={`absolute right-0 w-full bg-white/30 rounded-full hover:bg-white/50 active:bg-blue-500/80 transition-colors cursor-pointer ${isDraggingScroll ? 'bg-blue-500/80' : ''}`}
                                    style={{
                                        top: `${thumbTop}%`,
                                        height: `${scrollRatio * 100}%`,
                                        minHeight: '20px'
                                    }}
                                />
                            </div>
                        )}

                        {/* Error Overlay */}
                        {screenshotError && (
                            <div className="absolute inset-0 bg-black/80 flex items-center justify-center backdrop-blur-sm z-40">
                                <div className="bg-gray-900 p-6 rounded-lg border border-red-500/30 max-w-md text-center shadow-2xl">
                                    <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <TriangleAlert className="text-red-500" size={24} />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Stream Paused</h3>
                                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                        {screenshotError === "Restricted System Page"
                                            ? "Chrome security policy prevents capturing system pages (chrome://, newtab, etc). Navigate to a regular website to resume."
                                            : screenshotError}
                                    </p>
                                    <div className="flex justify-center space-x-3">
                                        <button
                                            onClick={() => handleNavigate('home')}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-md transition-colors"
                                        >
                                            Go Home
                                        </button>
                                        <button
                                            onClick={() => setScreenshotError(null)}
                                            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-md transition-colors"
                                        >
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </ChromeFrame>
            </div>

            {/* Status Bar */}
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-gray-800/80 backdrop-blur-sm rounded-full text-xs text-gray-400 border border-gray-700 pointer-events-none flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${streamMode === 'WEBRTC' ? 'bg-purple-500 animate-pulse' :
                        streamMode === 'MJPEG' ? 'bg-blue-500 animate-pulse' :
                            'bg-gray-500'
                        }`} />
                    <span className="font-medium text-gray-300">
                        {streamMode === 'WEBRTC' ? 'WebRTC Live' :
                            streamMode === 'MJPEG' ? 'MJPEG Live' : 'Static View'}
                    </span>
                </div>

                {streamMode === 'STATIC' && (
                    <>
                        <span className="text-gray-600">|</span>
                        <span className="text-green-400">
                            {refreshInterval < 1000 ? `${(1000 / refreshInterval).toFixed(1)} FPS` : `${(refreshInterval / 1000).toFixed(1)}s`}
                        </span>
                    </>
                )}

                {inputEnabled && (
                    <>
                        <span className="text-gray-600">|</span>
                        <span className="text-red-400 flex items-center gap-1">
                            <MousePointer2 size={10} /> Input Active
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export default MirrorView;
